import { useEffect, useMemo } from "react";
import { Button, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaFlagCheckered } from "react-icons/fa";
import BikeBackground from "../../assets/BikeBackground.jpeg";
import XcBackground from "../../assets/XC_Background_From_User.jpg";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	setSelectedEvent,
} from "../../store/slices/fantasySlice";
import {
	fetchSeasonsThunk,
	getEventsThunk,
} from "../../store/thunks/fantasyThunk";
import {
	getEventSports,
	getFutureEvents,
	getSeasonEvents,
} from "../../utils/helper";
import { Event, Sport } from "../../types/model";
import "./SelectDisciplinePage.css";

const getSportKey = (sport: Sport) => {
	const label = `${sport.shortName || ""} ${sport.name || ""}`.toLowerCase();
	if (
		label.includes("cross country") ||
		label.includes("cross-country") ||
		label.includes("xco") ||
		label.includes("xc")
	) {
		return "cross-country";
	}
	return "downhill";
};

const getSportLabel = (sport: Sport) => {
	const shortName = sport.shortName?.trim();
	if (shortName) {
		return shortName.toUpperCase();
	}
	return sport.name.toUpperCase();
};

const getDisciplineOrder = (sport: Sport) => {
	const label = `${sport.shortName || ""} ${sport.name || ""}`.toLowerCase();
	if (label.includes("dh") || label.includes("downhill")) {
		return 0;
	}
	if (label.includes("xc") || label.includes("cross")) {
		return 1;
	}
	return 2;
};

const SelectDisciplinePage = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { events, selectedSeason, eventLoading } = useAppSelector(
		(state: RootState) => state.fantasy,
	);

	useEffect(() => {
		if (events.length === 0) {
			dispatch(getEventsThunk());
		}
		if (!selectedSeason?._id) {
			dispatch(fetchSeasonsThunk());
		}
	}, [dispatch, events.length, selectedSeason?._id]);

	const activeEvents = useMemo(() => {
		const seasonEvents = getSeasonEvents(events, selectedSeason?._id ?? "");
		return getFutureEvents(seasonEvents);
	}, [events, selectedSeason?._id]);

	const activeSports = useMemo(() => {
		return [...getEventSports(activeEvents)].sort(
			(a, b) => getDisciplineOrder(a) - getDisciplineOrder(b),
		);
	}, [activeEvents]);

	const getNextEventForSport = (sportId: string): Event | undefined =>
		activeEvents.find((event) => event.sport?._id === sportId);

	const handleSelectDiscipline = (sport: Sport) => {
		const nextEvent = getNextEventForSport(sport._id);
		if (!nextEvent) {
			return;
		}

		dispatch(setSelectedEvent(nextEvent));
		navigate("/");
	};

	return (
		<main className="select-discipline-page">
			<Helmet>
				<title>Select Discipline - The Race Companion</title>
				<meta
					name="description"
					content="Choose a fantasy racing discipline on The Race Companion."
				/>
			</Helmet>

			{eventLoading && activeSports.length === 0 ? (
				<div className="select-discipline-page__loading">
					<Spinner animation="border" variant="light" />
				</div>
			) : activeSports.length === 0 ? (
				<div className="select-discipline-page__empty">
					No active disciplines are available.
				</div>
			) : (
				<div className="select-discipline-page__rows">
					{activeSports.map((sport) => {
						const nextEvent = getNextEventForSport(sport._id);
						const sportKey = getSportKey(sport);
						const backgroundImage =
							sportKey === "cross-country" ? XcBackground : BikeBackground;

						return (
							<Button
								key={sport._id}
								type="button"
								className={`select-discipline-row select-discipline-row--${sportKey}`}
								style={{ backgroundImage: `url(${backgroundImage})` }}
								onClick={() => handleSelectDiscipline(sport)}
							>
								<span className="select-discipline-row__shade" />
								<span className="select-discipline-row__content">
									<span className="select-discipline-row__eyebrow">
										<FaFlagCheckered />
										Select discipline
									</span>
									<span className="select-discipline-row__title">
										{getSportLabel(sport)}
									</span>
									<span className="select-discipline-row__meta">
										{nextEvent
											? `Next event: ${nextEvent.name}`
											: "No upcoming event"}
									</span>
								</span>
								<span className="select-discipline-row__action">
									<FaArrowRight />
								</span>
							</Button>
						);
					})}
				</div>
			)}
		</main>
	);
};

export default SelectDisciplinePage;
